import React, { useState } from 'react';
import CheckboxField from '../../../../Components/Form/components/CheckboxField';
import FormCondition from '../../../../Components/Form/FormCondition';
import CSSZSickLeaveCertificateOutingsFields from '../CSSZFields/CSSZSickLeaveCertificateOutingsFields';
import DateField from '../../../../Components/Form/components/DateField';
import FormWrapper from '../../../../Components/Form/FormWrapper';
import { useForm, useWatch } from 'react-hook-form';
import {
	CSSZAddress,
	CSSZPermittedWalks,
} from '../../Interfaces/CSSZ.interface';
import SubmitButton from '../../../../Components/Button/SubmitButton';
import DiagnosisSelectField from '../../../../Components/Base/Diagnosis/DiagnosisSelectField';
import FormContainer from '../../../../Components/Form/FormContainer';
import { SelectValue } from '../../../../Components/Base/Select/Select';
import styles from './CSSZSickLeaveCertificateAttendingDoctorReportForm.module.scss';
import { useDisclose } from '../../../Modal/Hooks/useDisclose';
import CSSZSickLeavePatientAddressFormModal from '../CSSZSickLeavePatientAddressForm/CSSZSickLeavePatientAddressFormModal';
import CSSZAddressPreview from '../CSSZSickLeaveCertificateCreate/FormParts/CSSZAddressPreview';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import InputField from '../../../../Components/Form/components/InputField';
import SelectField from '../../../../Components/Form/components/SelectField';
import { DeepPartial } from '../../../../Interfaces/DeepPartial';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { meddiYup } from '../../../Base/Validation/meddiYup';
import {
	CSSZSickLeavePatientAddressValidationScheme,
	CSSZPermittedWalksValidationSchema,
} from '../../Validation/CSSZValidation';
import { isCSSZAddressComplete } from '../../Helpers/CSSZ.helper';
import TopFieldContent from '../../../../Components/Form/components/TopFieldContent';
import Tooltip from '../../../../Components/Popup/Tooltip';
import Text from '../../../../Components/Base/Text/Text';

export interface CSSZSickLeaveCertificateAttendingDoctorReportFormValues {
	allowOrChangeOutings: boolean;
	allowOrChangeOutingsData?: CSSZPermittedWalks;
	cancelOutings: boolean;
	cancelOutingsData?: {
		dateFrom: string;
	};
	individualOutings: boolean;
	changeDiagnosis: boolean;
	changeDiagnosisData?: {
		dateFrom: string;
		diagnosis: SelectValue;
	};
	allowAddressChange: boolean;
	allowAddressChangeData?: {
		patientAddress: CSSZAddress;
		validFrom: string;
		validTo?: string;
	};
	violationOfSickLeaveCertificate: boolean;
	violationOfSickLeaveCertificateData: {
		note?: string;
	};
	release: boolean;
	releaseData?: {
		dateFrom: string;
		forwardedTo: SelectValue;
	};
	hospitalityChange: boolean;
	hospitalityChangeData?: {
		dateFrom: string;
		dateTo?: string;
	};
}

interface Props {
	onSubmit: (
		values: CSSZSickLeaveCertificateAttendingDoctorReportFormValues,
	) => void;
	defaultValues?: DeepPartial<CSSZSickLeaveCertificateAttendingDoctorReportFormValues>;
	individualOutingsLocked?: boolean;
	patientId: string;
}

interface DisabledFields {
	allowOrChangeOutings: boolean;
	cancelOutings: boolean;
	individualOutings: boolean;
}

const DISABLED_FIELDS_DEFAULT_STATE = {
	allowOrChangeOutings: false,
	cancelOutings: false,
	individualOutings: false,
};

const MOCK_FORWARDED_TO_OPTIONS: SelectValue[] = [
	{
		label: 'Endokrinologie',
		value: '59cb8c99-ee70-485f-b21a-23c772e6fb63',
	},
];

const validationSchema = yup
	.object({
		allowOrChangeOutings: yup.boolean(),
		allowOrChangeOutingsData: yup
			.mixed<CSSZPermittedWalks>()
			.when('allowOrChangeOutings', {
				is: true,
				then: () => CSSZPermittedWalksValidationSchema,
			}),
		cancelOutings: yup.boolean(),
		cancelOutingsData: yup.mixed().when('cancelOutings', {
			is: true,
			then: () =>
				yup.object({
					dateFrom: yup
						.string()
						.required(
							'validation.CSSZSickLeaveCertificateAttendingDoctorReportForm.cancelOutingsData.dateFrom',
						),
				}),
		}),
		individualOutings: yup.boolean(),
		changeDiagnosis: yup.boolean(),
		changeDiagnosisData: yup.mixed().when('changeDiagnosis', {
			is: true,
			then: () =>
				yup.object({
					dateFrom: yup
						.string()
						.required(
							'validation.CSSZSickLeaveCertificateAttendingDoctorReportForm.changeDiagnosis.dateFrom',
						),
					diagnosis: meddiYup.selectRequired(
						'validation.CSSZSickLeaveCertificateAttendingDoctorReportForm.changeDiagnosis.diagnosis',
					),
				}),
		}),
		allowAddressChange: yup.boolean(),
		allowAddressChangeData: yup.mixed().when('allowAddressChange', {
			is: true,
			then: () =>
				yup.object({
					patientAddress: CSSZSickLeavePatientAddressValidationScheme,
					validFrom: yup
						.string()
						.required(
							'validation.CSSZSickLeaveCertificateAttendingDoctorReportForm.allowAddressChangeData.validFrom',
						),
					validTo: yup.string().optional(),
				}),
		}),
		violationOfSickLeaveCertificate: yup.boolean(),
		violationOfSickLeaveCertificateData: yup
			.mixed()
			.when('violationOfSickLeaveCertificate', {
				is: true,
				then: () =>
					yup.object({
						note: yup.string().optional(),
					}),
			}),
		release: yup.boolean(),
		releaseData: yup.mixed().when('release', {
			is: true,
			then: () =>
				yup.object({
					dateFrom: yup
						.string()
						.required(
							'validation.CSSZSickLeaveCertificateAttendingDoctorReportForm.releaseData.dateFrom',
						),
					forwardedTo: meddiYup.selectRequired(
						'validation.CSSZSickLeaveCertificateAttendingDoctorReportForm.releaseData.forwardedTo',
					),
				}),
		}),
		hospitalityChange: yup.boolean(),
		hospitalityChangeData: yup.mixed().when('hospitalityChange', {
			is: true,
			then: () =>
				yup.object({
					dateFrom: yup
						.string()
						.required(
							'validation.CSSZSickLeaveCertificateAttendingDoctorReportForm.hospitalityChangeData.dateFrom',
						),
					dateTo: yup.string().optional(),
				}),
		}),
	})
	.test(
		'at-least-one-outings-option',
		'validation.CSSZSickLeaveCertificateAttendingDoctorReportForm.atLeastOneOutingsOption',
		value =>
			Boolean(
				value?.release ||
					value?.allowOrChangeOutings ||
					value?.cancelOutings ||
					value?.individualOutings,
			),
	);

const CSSZSickLeaveCertificateAttendingDoctorReportForm: React.FC<Props> = ({
	onSubmit,
	defaultValues,
	individualOutingsLocked = false,
	patientId,
}) => {
	const {
		isOpen: isAddressEditOpen,
		onOpen: onOpenAddressEdit,
		onClose: onCloseAddressEdit,
	} = useDisclose();
	const [disabledFields, setDisabledFields] = useState<DisabledFields>(
		DISABLED_FIELDS_DEFAULT_STATE,
	);

	const changeAllowedFieldsByKey = (
		fieldKey: keyof DisabledFields,
		value: boolean,
	) => {
		setDisabledFields(() => {
			if (!value) {
				return DISABLED_FIELDS_DEFAULT_STATE;
			}

			return {
				allowOrChangeOutings: fieldKey !== 'allowOrChangeOutings',
				cancelOutings: fieldKey !== 'cancelOutings',
				individualOutings: fieldKey !== 'individualOutings',
			};
		});
	};

	const formMethods = useForm({
		defaultValues,
		resolver: yupResolver(validationSchema),
	});

	const address = useWatch({
		control: formMethods.control,
		name: 'allowAddressChangeData.patientAddress',
		defaultValue: defaultValues?.allowAddressChangeData?.patientAddress,
	});

	const handleSubmitAddressValues = (values: CSSZAddress) => {
		formMethods.setValue('allowAddressChangeData.patientAddress', values);
		onCloseAddressEdit();
	};

	const individualOutingsCheckbox = (
		<CheckboxField
			name='individualOutings'
			tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.individualOutings'
			isWrapped
			customOnChange={value =>
				changeAllowedFieldsByKey('individualOutings', value)
			}
			isDisabled={disabledFields.individualOutings || individualOutingsLocked}
		/>
	);

	return (
		<>
			<FormWrapper formMethods={formMethods}>
				<CheckboxField
					name='allowOrChangeOutings'
					tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.allowOrChangeOutings'
					isWrapped
					customOnChange={value =>
						changeAllowedFieldsByKey('allowOrChangeOutings', value)
					}
					isDisabled={disabledFields.allowOrChangeOutings}
				/>
				<FormCondition controlName='allowOrChangeOutings' equal={true}>
					<CSSZSickLeaveCertificateOutingsFields containerName='allowOrChangeOutingsData' />
				</FormCondition>
				<CheckboxField
					name='cancelOutings'
					tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.cancelOutings'
					isWrapped
					customOnChange={value =>
						changeAllowedFieldsByKey('cancelOutings', value)
					}
					isDisabled={disabledFields.cancelOutings}
				/>
				<FormCondition controlName='cancelOutings' equal={true}>
					<FormContainer name='cancelOutingsData'>
						<DateField
							name='dateFrom'
							label='component.CSSZSickLeaveCertificateAttendingDoctorReport.cancelOutingsDateFrom'
							isRequired
							isWrapped
						/>
					</FormContainer>
				</FormCondition>
				{individualOutingsLocked ? (
					<Tooltip
						position='bottom left'
						trigger={
							<div style={{ width: '100%' }}>{individualOutingsCheckbox}</div>
						}>
						<Text tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.individualOutingsLockedTooltip' />
					</Tooltip>
				) : (
					individualOutingsCheckbox
				)}
				<CheckboxField
					name='changeDiagnosis'
					tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.changeDiagnosis'
					isWrapped
				/>
				<FormCondition controlName='changeDiagnosis' equal={true}>
					<FormContainer name='changeDiagnosisData' className={styles.inline}>
						<DateField
							name='dateFrom'
							label='component.CSSZSickLeaveCertificateAttendingDoctorReport.changeDiagnosisDateFrom'
							isRequired
							isWrapped
						/>
						<DiagnosisSelectField
							name='diagnosis'
							label='component.CSSZSickLeaveCertificateAttendingDoctorReport.changeDiagnosisDiagnosis'
							isRequired
							isWrapped
						/>
					</FormContainer>
				</FormCondition>
				<CheckboxField
					name='allowAddressChange'
					tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.allowAddressChange'
					isWrapped
				/>
				<FormCondition controlName='allowAddressChange' equal={true}>
					<FormContainer name='allowAddressChangeData'>
						<div className={styles.addressSection}>
							<TopFieldContent
								label='component.CSSZSickLeaveCertificateAttendingDoctorReport.allowAddressChangeAddress'
								hideRequired
							/>
							<div className={styles.addressWrapper}>
								{address && isCSSZAddressComplete(address) ? (
									<>
										<CSSZAddressPreview address={address as CSSZAddress} />
										<LinkButton
											title='base.shared.edit'
											theme={Theme.BLUE}
											onClick={onOpenAddressEdit}
										/>
									</>
								) : (
									<LinkButton
										title='component.CSSZSickLeaveCertificateAttendingDoctorReport.enterNewAddress'
										theme={Theme.BLUE}
										onClick={onOpenAddressEdit}
									/>
								)}
							</div>
						</div>
						<div className={styles.inline}>
							<DateField
								name='validFrom'
								label='component.CSSZSickLeaveCertificateAttendingDoctorReport.allowAddressChangeValidFrom'
								isRequired
								isWrapped
							/>
							<DateField
								name='validTo'
								label='component.CSSZSickLeaveCertificateAttendingDoctorReport.allowAddressChangeValidTo'
								isWrapped
							/>
						</div>
					</FormContainer>
				</FormCondition>
				<CheckboxField
					name='violationOfSickLeaveCertificate'
					tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.violationOfSickLeaveCertificate'
					isWrapped
				/>
				<FormCondition
					controlName='violationOfSickLeaveCertificate'
					equal={true}>
					<FormContainer name='violationOfSickLeaveCertificateData'>
						<InputField
							name='note'
							label='component.CSSZSickLeaveCertificateAttendingDoctorReport.violationOfSickLeaveCertificateNote'
							isWrapped
						/>
					</FormContainer>
				</FormCondition>
				<CheckboxField
					name='release'
					tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.release'
					isWrapped
				/>
				<FormCondition controlName='release' equal={true}>
					<FormContainer name='releaseData' className={styles.inline}>
						<DateField
							name='dateFrom'
							label='component.CSSZSickLeaveCertificateAttendingDoctorReport.releaseDateFrom'
							isWrapped
							isRequired
						/>
						<SelectField
							name='forwardedTo'
							label='component.CSSZSickLeaveCertificateAttendingDoctorReport.releaseForwardedTo'
							isWrapped
							isRequired
							options={MOCK_FORWARDED_TO_OPTIONS}
						/>
					</FormContainer>
				</FormCondition>
				<CheckboxField
					name='hospitalityChange'
					tx='component.CSSZSickLeaveCertificateAttendingDoctorReport.hospitalityChange'
					isWrapped
				/>
				<FormCondition controlName='hospitalityChange' equal={true}>
					<FormContainer name='hospitalityChangeData' className={styles.inline}>
						<DateField
							name='dateFrom'
							label='component.CSSZSickLeaveCertificateAttendingDoctorReport.hospitalityChangeDateFrom'
							isWrapped
							isRequired
						/>
						<DateField
							name='dateTo'
							label='component.CSSZSickLeaveCertificateAttendingDoctorReport.hospitalityChangeDateTo'
							isWrapped
						/>
					</FormContainer>
				</FormCondition>
				<div className='mt-3'>
					<SubmitButton onSubmit={onSubmit} />
				</div>
			</FormWrapper>
			{isAddressEditOpen && (
				<CSSZSickLeavePatientAddressFormModal
					isOpen
					userId={patientId}
					onClose={onCloseAddressEdit}
					onSubmit={handleSubmitAddressValues}
					collectedValues={{
						patientAddress: address as DeepPartial<CSSZAddress>,
					}}
				/>
			)}
		</>
	);
};

export default CSSZSickLeaveCertificateAttendingDoctorReportForm;
